import { useState, useEffect, useCallback, useMemo } from "react";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { useProjectMilestoneApi } from "../../../hooks/useProjectMilestone.js";
import { useParams } from "react-router-dom";
import { useRbac } from "../../../hooks/RbacContext";

export const useAllProjectMilestoneViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const projectId = useParams()?.id;
  const { permissions } = useRbac();

  const [dateValidation,setDateValidation] = useState({})
  const {
    getProjects,
    cacheOperations: { readProjects, writeProjects },
  } = useProjectApi();

  const {
    getProjectMilestones,
    createProjectMilestone: apiCreateProjectMilestone,
    updateProjectMilestone: apiUpdateProjectMilestone,
    deleteProjectMilestone,
    getProjectMilestonesByProjectId,
    cacheOperations: { readProjectMilestones, writeProjectMilestones },
  } = useProjectMilestoneApi();

  const superAdminRoleIds = process.env.REACT_APP_SUPER_ADMIN_ROLE_ID
    ? process.env.REACT_APP_SUPER_ADMIN_ROLE_ID.split(",").map(id => parseInt(id.trim()))
    : [];

  const isSuperAdmin = superAdminRoleIds.includes(parseInt(permissions?.globalRole?.role_id));

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const [projectsResult, projectMilestonesResult] = await Promise.all([
        getProjects(isSuperAdmin),
        projectId ? getProjectMilestonesByProjectId(projectId) : getProjectMilestones()
      ]);

      if (!projectsResult.success || !projectMilestonesResult.success) {
        throw new Error("Failed to fetch required data");
      }
      writeProjects(projectsResult.data);
      writeProjectMilestones(projectMilestonesResult.data)
      if (projectMilestonesResult.data.length == 0) {
        const projectDetails = projectsResult.data.find(item => item.project_id === projectId);
        const projectMinDate = projectDetails ? projectDetails.proposed_start_date : 'today';
        const projectMaxDate = projectDetails ? projectDetails.proposed_end_date : 'today';
        setDateValidation({
          minDate: projectMinDate,
          maxDate:projectMaxDate
        })

      } else
      
        setDateValidation({
          minDate: projectMilestonesResult.data[0]?.milestone_min_date,
          maxDate:projectMilestonesResult?.data[0]?.milestone_max_date
        })

    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [getProjects, getProjectMilestones, getProjectMilestonesByProjectId, writeProjects, writeProjectMilestones, loading]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateProjectMilestone = useCallback(
    async (formData) => {
      try {
        const result = await apiCreateProjectMilestone(formData);
        if (result.success) {
          return { success: true };
        }
        throw new Error(result.error || "Failed to create project resource mapping");
      } catch (error) {
        console.error("Error creating  project resource mapping:", error);
        throw new Error(error.message);
      }
    },
    [apiCreateProjectMilestone, fetchData]
  );

  const handleUpdateProjectMilestone = useCallback(
    async (projectMilestone, formData) => {
      try {
        const result = await apiUpdateProjectMilestone(projectMilestone.milestone_id, formData);
        if (result.success) {
          await fetchData()
          return { success: true };
        }
        throw new Error(result.error || "Failed to update project resource mapping");
      } catch (error) {
        console.error("Error updating project resource mapping:", error);
        throw new Error(error.message);
      }
    },
    [apiUpdateProjectMilestone, fetchData]
  );

  const handleDelete = useCallback(
    async (projectMilestone) => {
      try {
        const result = await deleteProjectMilestone(projectMilestone.milestone_id);
        if (result.success) {
          await fetchData();
        } else {
          // setError(result.error);
          throw new Error(result.error);
        }
      } catch (err) {
        // setError(err.message);

        throw new Error(err);
      }
    },
    [deleteProjectMilestone, fetchData]
  );

  const getProjectName = useMemo(
    () => (projectId) => {
      const projects = readProjects(isSuperAdmin);

      const project = projects.find(
        (project) => project.project_id.toString() === projectId?.toString()
      );

      return project?.project_name || "Unknown";
    },
    [readProjects]
  );


  return {
    projectMilestones: readProjectMilestones(),
    projects: readProjects(isSuperAdmin),
    loading,
    error,
    getProjectName,
    createProjectMilestone: handleCreateProjectMilestone,
    updateProjectMilestone: handleUpdateProjectMilestone,
    handleDelete,
    refetch: fetchData,
    milestoneMinDate: dateValidation.minDate,
    milestoneMaxDate: dateValidation.maxDate
  };
};
